import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Skeleton } from "../components/ui/Skeleton";
import { useDeletePost } from "../hooks/useDeletePost";
import { toast } from "../lib/toast";

export const linkedinUrl = (urn?: string, id?: string): string | null => {
  const value = urn ?? id;
  if (!value) return null;
  if (/^urn:li:(share|activity|ugcPost):\d+$/.test(value)) {
    return `https://www.linkedin.com/feed/update/${value}`;
  }
  if (/^\d+$/.test(value)) {
    return `https://www.linkedin.com/feed/update/urn:li:share:${value}`;
  }
  return null;
};

const Published = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["published"],
    queryFn: () => api.posts.published(),
  });

  const deletePost = useDeletePost();
  const [deleteCandidate, setDeleteCandidate] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Publicados</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400">
          Posts publicados no LinkedIn.
        </p>
      </div>
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 space-y-2"
            >
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      )}
      {!isLoading && (!data || data.length === 0) && (
        <EmptyState
          title="Nada publicado ainda"
          message="Aprove um draft em /drafts e ele aparece aqui após a publicação."
        />
      )}
      <ConfirmDialog
        open={deleteCandidate !== null}
        title="Deletar este post do LinkedIn?"
        description="Esta ação é IRREVERSÍVEL — o post sairá do feed do LinkedIn."
        confirmLabel="Deletar"
        cancelLabel="Cancelar"
        destructive
        loading={deletePost.isPending}
        onConfirm={() => {
          if (deleteCandidate) {
            deletePost.mutate(
              { id: deleteCandidate, kind: "published" },
              {
                onSuccess: () =>
                  toast.success("Post deletado do LinkedIn"),
                onError: (err: Error) =>
                  toast.error("Falha ao deletar do LinkedIn", { description: err.message }),
                onSettled: () => setDeleteCandidate(null),
              }
            );
          }
        }}
        onCancel={() => setDeleteCandidate(null)}
      />
      {data && data.length > 0 && (
        <div className="space-y-3">
          {data.map((d) => {
            const url = linkedinUrl(d.linkedinPostUrn, d.linkedinPostId);
            return (
              <div
                key={d.id}
                data-testid={`post-${d.id}`}
                className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4"
              >
                <div className="flex items-center justify-between mb-2 gap-3">
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">
                    Publicado:{" "}
                    {d.publishedAt ? new Date(d.publishedAt).toLocaleString("pt-BR") : "?"}
                  </div>
                  <div className="flex items-center gap-2">
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs inline-flex items-center gap-1 text-[#0a66c2] dark:text-[#5fa8ed] hover:underline px-2 h-7"
                      >
                        <ExternalLink size={12} /> Ver no LinkedIn
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => setDeleteCandidate(d.id)}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline px-2 h-7 inline-flex items-center"
                    >
                      Deletar
                    </button>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap break-words text-zinc-900 dark:text-zinc-100 leading-relaxed">
                  {d.content}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Published;
